import React from 'react'
import Link from 'next/link'
import { categorySlug, groupByCategory } from 'utils/handyman-price-data'

// Grouped price list, shared by the service hubs: handyman, electrician and
// plumber job prices, and the part-time helper hourly rates. Items are
// { itemName, serviceCost, category, subCategory? }, grouped by category.
//
// Props:
//  - handymanPriceList + type: filter the full list by technicianType
//    ("all" = no filter). This was the original API and is still used by the plumber page.
//  - items: an already-chosen list, e.g. the "popular jobs" teaser on the hubs.
//    It takes precedence over handymanPriceList/type.
//  - title / subtitle: override the default heading copy.
//  - showCategoryAnchors: give each category an id so a table of contents can jump to it.
//  - moreHref / moreLabel: a "see full price list" link under the list.
export const PriceList = (props) => {
    const {
        handymanPriceList,
        type = 'all',
        items,
        title,
        subtitle,
        showCategoryAnchors = false,
        moreHref,
        moreLabel,
        id,
    } = props

    const filteredList = items
        ? items
        : type === 'all'
            ? (handymanPriceList || [])
            : (handymanPriceList || []).filter(item => item.technicianType === type)

    // Guard: a failed fetch used to crash the whole page on .filter of undefined.
    if (filteredList.length === 0) return null

    const groups = groupByCategory(filteredList)
    const label = type === 'all' ? 'Handyman' : type

    return (
        <section id={id} className="bg-gray-100 py-10">
            <div className="max-w-5xl mx-auto px-4">
                <div className="text-center mb-6">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                        {title || `${label} Service Price List`}
                    </h2>
                    <p className="text-sm text-gray-600 max-w-2xl mx-auto">
                        {subtitle || `Upfront fixed pricing for professional ${label.toLowerCase()} services in Singapore`}
                    </p>
                </div>

                <div className="space-y-4">
                    {groups.map(({ category, items: rows }) => (
                        <div
                            key={category}
                            id={showCategoryAnchors ? `prices-${categorySlug(category)}` : undefined}
                            className="bg-white rounded-2xl border border-gray-200 overflow-hidden scroll-mt-20"
                        >
                            <div className="bg-linear-to-r from-orange-600 to-amber-400 px-4 py-2.5">
                                <h3 className="text-sm font-bold text-white uppercase tracking-wide">{category}</h3>
                            </div>
                            {/* Compact rows: each item was a ~140px card, which made this
                                section ~3000px tall on mobile for 22 services. */}
                            <ul className="divide-y divide-gray-100">
                                {rows.map((item) => (
                                    <li key={item.itemName} className="flex items-center justify-between gap-3 px-4 py-2.5">
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-gray-900 leading-snug">
                                                {item.itemName}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-0.5">{item.subCategory}</p>
                                        </div>
                                        <span className="shrink-0 text-sm font-bold text-green-700 whitespace-nowrap">
                                            S$ {Number(item.serviceCost).toFixed(2)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {moreHref &&
                    <div className="text-center pt-6">
                        <Link
                            href={moreHref}
                            prefetch={false}
                            className="inline-flex items-center justify-center min-h-12 px-6 rounded-full bg-gray-900 text-white text-sm font-bold"
                        >
                            {moreLabel || 'See the full price list'}
                        </Link>
                    </div>
                }
            </div>
        </section>
    )
}
